'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { UserWorkspace, getUserWorkspaces } from '@/services/api/workspaces'
import { useAuth } from './AuthContext'

interface WorkspaceContextType {
  currentWorkspace: string
  availableWorkspaces: UserWorkspace[]
  isLoadingWorkspaces: boolean
  hasWorkspaceAccess: boolean
  setCurrentWorkspace: (workspaceCode: string) => void
  refreshWorkspaces: () => Promise<void>
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined)

interface WorkspaceProviderProps {
  children: ReactNode
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { isAuthenticated, user } = useAuth()
  
  // Initialize currentWorkspace from localStorage or default to APP_PUBLIC
  const [currentWorkspace, setCurrentWorkspaceState] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('currentWorkspace') || 'APP_PUBLIC'
    }
    return 'APP_PUBLIC'
  })
  
  const [availableWorkspaces, setAvailableWorkspaces] = useState<UserWorkspace[]>([])
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(false)
  const [hasWorkspaceAccess, setHasWorkspaceAccess] = useState(true)

  // Custom setCurrentWorkspace that also saves to localStorage
  const setCurrentWorkspace = useCallback((workspaceCode: string) => {
    setCurrentWorkspaceState(workspaceCode)
    if (typeof window !== 'undefined') {
      localStorage.setItem('currentWorkspace', workspaceCode)
    }
  }, [currentWorkspace])

  const refreshWorkspaces = useCallback(async () => {
    if (!isAuthenticated || !user) return


    try {
      setIsLoadingWorkspaces(true)
      
      // Get saved workspace from localStorage
      const savedWorkspace = typeof window !== 'undefined' ? localStorage.getItem('currentWorkspace') : null
      
      // Superadmin can access APP_PUBLIC workspace and any assigned workspaces
      if (user.permissions?.includes('superadmin')) {
        let workspaces: UserWorkspace[] = []
        try {
          // Try to get assigned workspaces for superadmin too
          workspaces = await getUserWorkspaces()
          setAvailableWorkspaces(workspaces)
        } catch (error) {
          // If API fails, superadmin still has APP_PUBLIC access
          console.warn('Failed to load workspaces for superadmin, using APP_PUBLIC only:', error)
          setAvailableWorkspaces([])
        }
        
        // For superadmin, use saved workspace if valid, otherwise default to APP_PUBLIC
        if (savedWorkspace) {
          // Check if saved workspace is valid (either APP_PUBLIC or in available workspaces)
          const isValidWorkspace = savedWorkspace === 'APP_PUBLIC' || 
            workspaces.some(w => w.code === savedWorkspace)
          
          if (isValidWorkspace) {
            setCurrentWorkspace(savedWorkspace)
          } else {
            setCurrentWorkspace('APP_PUBLIC')
          }
        } else {
          setCurrentWorkspace('APP_PUBLIC')
        }
        
        setHasWorkspaceAccess(true)
        return
      }

      // For admin users, get their assigned workspaces
      const workspaces = await getUserWorkspaces()
      setAvailableWorkspaces(workspaces)
      
      if (workspaces.length > 0) {
        // Admin has workspaces - preserve current workspace if valid, use saved if available, otherwise use first available
        if (savedWorkspace) {
          // If there's a saved workspace, check if it's valid
          if (workspaces.some(w => w.code === savedWorkspace)) {
            setCurrentWorkspace(savedWorkspace)
          } else {
            // Saved workspace is invalid, use first available
            setCurrentWorkspace(workspaces[0].code)
          }
        } else {
          // No saved workspace - check if current workspace is valid
          const currentIsValid = workspaces.some(w => w.code === currentWorkspace)
          if (currentIsValid) {
            // Current workspace is valid, keep it and save to localStorage
            setCurrentWorkspace(currentWorkspace)
          } else {
            // Current workspace is invalid, use first available
            setCurrentWorkspace(workspaces[0].code)
          }
        }
        setHasWorkspaceAccess(true)
      } else {
        // Admin has no workspaces - deny access
        setCurrentWorkspace('')
        setHasWorkspaceAccess(false)
      }
    } catch (error) {
      console.error('Failed to load user workspaces:', error)
      // On error, deny access for admin users
      if (user.permissions?.includes('admin')) {
        setCurrentWorkspace('')
        setHasWorkspaceAccess(false)
      } else {
        // Fallback to APP_PUBLIC for superadmin on error
        setCurrentWorkspace('APP_PUBLIC')
        setHasWorkspaceAccess(true)
      }
      setAvailableWorkspaces([])
    } finally {
      setIsLoadingWorkspaces(false)
    }
  }, [isAuthenticated, user, setCurrentWorkspace, currentWorkspace])

  // Load workspaces when user authenticates
  useEffect(() => {
    
    if (isAuthenticated && user) {
      refreshWorkspaces()
    }
    // Don't reset workspace during loading - only reset when explicitly unauthenticated
    // The workspace should persist during page refresh while authentication is loading
  }, [isAuthenticated, user, refreshWorkspaces])

  // Reset workspace only when user explicitly logs out (not during loading)
  useEffect(() => {
    // Only reset if we're explicitly unauthenticated (not loading)
    if (!isAuthenticated && user === null && typeof window !== 'undefined') {
      const hasToken = localStorage.getItem('access_token')
      if (!hasToken) {
        setCurrentWorkspace('APP_PUBLIC')
        setAvailableWorkspaces([])
        setHasWorkspaceAccess(true)
      }
    }
  }, [isAuthenticated, user, setCurrentWorkspace])

  const value: WorkspaceContextType = {
    currentWorkspace,
    availableWorkspaces,
    isLoadingWorkspaces,
    hasWorkspaceAccess,
    setCurrentWorkspace,
    refreshWorkspaces
  }

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  )
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext)
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider')
  }
  return context
} 